/**
 * The right rail of the agent-opened state (spec #44 §1.3).
 *
 * Five things about THIS agent, each one a record: its bets
 * (`commitment.owner_domain`), its lane from the plan (which is joinable
 * only because of accepted delta 3's `lanes[].owner_domain`), the tools it
 * owns including the ones it may not act on, the specialists that report
 * into it, and a one-line pin of what it learned most recently.
 *
 * The two per-agent reads (ownership, hero) happen here rather than in the
 * provider on purpose: they are one-shot reads for the channel that is
 * open, not part of the polled loop.
 */

import { useEffect, useState } from 'react';
import { SkeletonBodyText, Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import { betsByDomain, planLaneForDomain } from '../../contexts/workspaceComposition';
import { useWorkspace } from '../../hooks/useWorkspace';
import { getMemoryHero } from '../../services/agentMindApi';
import { getAgentOwnership } from '../../services/workspaceApi';
import type { MemoryHeroResponse, OwnershipRow } from '../../types/agentMind';
import { BetCard } from './BetCard';
import { LearningPin } from './LearningPin';
import { OwnsChips } from './OwnsChips';
import styles from './workspace.module.css';

export function EffectRail({ domain }: { domain: string }) {
  const { bets, plan, specialists, loading } = useWorkspace();
  const [ownership, setOwnership] = useState<OwnershipRow[] | null>(null);
  const [hero, setHero] = useState<MemoryHeroResponse | null>(null);
  const [ownershipError, setOwnershipError] = useState<string | null>(null);
  const [heroError, setHeroError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOwnership(null);
    setHero(null);
    setOwnershipError(null);
    setHeroError(null);

    getAgentOwnership(domain)
      .then((response) => {
        if (!cancelled) setOwnership(response.items ?? []);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setOwnershipError(
            err instanceof Error ? err.message : 'Could not read ownership'
          );
        }
      });

    getMemoryHero(domain)
      .then((response) => {
        if (!cancelled) setHero(response);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setHeroError(
            err instanceof Error ? err.message : 'Could not read the hero'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [domain]);

  const domainBets = betsByDomain(bets)[domain] ?? [];
  const lane = planLaneForDomain(plan, domain);
  const reporting = specialists.filter((s) => s.domain === domain);

  return (
    <aside className={styles.rail} aria-label="Effect rail">
      <section className={styles.railSection}>
        <div className={styles.railHead}>What I learned most recently</div>
        <LearningPin hero={hero} error={heroError} />
      </section>

      <section className={styles.railSection}>
        <div className={styles.railHead}>Its lane</div>
        <div data-testid="rail-lane">
          {lane ? (
            <>
              <Text as="p" fontWeight="semibold">
                {lane.display_name}
              </Text>
              {lane.mandate ? (
                <div className={styles.subtle}>{lane.mandate}</div>
              ) : null}
              {lane.blockers.length > 0 ? (
                <div className={styles.subtle}>
                  Blocked by: {lane.blockers.join(', ')}
                </div>
              ) : null}
            </>
          ) : (
            <Text as="p" tone="subdued">
              No lane in the current plan names this agent.
            </Text>
          )}
        </div>
      </section>

      <section className={styles.railSection}>
        <div className={styles.railHead}>Its bets</div>
        {loading.plan && domainBets.length === 0 ? (
          <SkeletonBodyText lines={3} />
        ) : domainBets.length === 0 ? (
          <Text as="p" tone="subdued">
            No bet in the plan is owned by this agent.
          </Text>
        ) : (
          domainBets.map((bet) => <BetCard key={bet.id} bet={bet} />)
        )}
      </section>

      <section className={styles.railSection}>
        <div className={styles.railHead}>Owns</div>
        {ownershipError ? (
          <Text as="p" tone="subdued">
            Ownership could not be read.
          </Text>
        ) : ownership === null ? (
          <SkeletonBodyText lines={1} />
        ) : (
          <OwnsChips rows={ownership} />
        )}
      </section>

      <section className={styles.railSection}>
        <div className={styles.railHead}>Reports into this channel</div>
        {reporting.length === 0 ? (
          <Text as="p" tone="subdued">
            No specialist reports into this agent yet.
          </Text>
        ) : (
          <ul>
            {reporting.map((specialist) => (
              <li key={specialist.key}>
                <Link to={`/marketing-agents/s/${specialist.key}`}>
                  {specialist.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}
